const DEFAULT_MAX_RETRY_CHECKS = 10;
/**
 * Manages retry logic for ADF (Atlassian Document Format) streaming operations.
 *
 * This class provides automatic retry functionality for streaming requests that may fail
 * or encounter errors during processing. It monitors for errors at regular intervals
 * and can automatically retry failed requests up to a specified maximum number of attempts.
 *
 * @example
 * ```typescript
 * const retryManager = new ADFStreamingRetryManager({
 *   maxRetries: 3,
 *   delayMs: 1000,
 *   beforeRetryCallback: () => console.log('Retrying...'),
 *   fetcher: (abortController) => startStreamingRequest(abortController),
 *   errorChecker: () => hasStreamingError()
 * });
 * ```
 */
export class ADFStreamingRetryManager {
	/** Timer ID for the error checking interval, null when no timer is active */
	private errorCheckTimerId: ReturnType<typeof setTimeout> | null = null;

	/** Current number of retry attempts made */
	private retries = 0;

	/** AbortController for the current request, used to cancel ongoing operations */
	private abortController: AbortController | null = null;

	/**
	 * Maximum number of retry checks after fetching started.
	 * This is to prevent infinite loop of checking for errors.
	 */
	private maxRetryChecks = DEFAULT_MAX_RETRY_CHECKS;
	private currentRetryCheck = 0;

	/**
	 * Creates a new ADFStreamingRetryManager instance.
	 *
	 * @param options - Configuration options for the retry manager
	 * @param options.maxRetries - Maximum number of retry attempts allowed
	 * @param options.delayMs - Delay in milliseconds between error checks and retries
	 * @param options.beforeRetryCallback - Callback executed before each retry attempt.
	 *   Must not throw errors or the fetcher will not be called.
	 * @param options.fetcher - Function that initiates the streaming request.
	 *   Must not throw errors or subsequent retries won't be triggered.
	 *   Receives an AbortController to allow request cancellation.
	 * @param options.errorChecker - Function that checks if an error has occurred.
	 *   Must not throw errors or the previous request won't be aborted and retry won't be attempted.
	 *   Should return true if an error is detected, false otherwise.
	 */
	constructor(
		private readonly options: {
			/**
			 * beforeRetryCallback must not throw error otherwise fetcher will not be called.
			 */
			beforeRetryCallback: () => void;
			delayMs: number;
			/**
			 * errorChecker must not throw error previous request won't be aborted
			 *  and retry won't be attempted.
			 */
			errorChecker: () => boolean;
			/**
			 * Fetcher must not throw error otherwise follow up retry won't be triggered.
			 * If it throws error, then follow up retry won't be triggered.
			 */
			fetcher: (abortController: AbortController, retryManager?: ADFStreamingRetryManager) => void;
			maxRetries: number;
			maxRetryChecks?: number;
		},
	) {
		this.maxRetryChecks = options.maxRetryChecks ?? DEFAULT_MAX_RETRY_CHECKS;
	}

	/**
	 * Starts a timer to check for errors after the specified delay.
	 * When the timer expires, it will attempt a retry if an error is detected.
	 *
	 * @private
	 */
	private startErrorCheckTimer() {
		this.clearErrorCheckTimer();
		this.errorCheckTimerId = setTimeout(() => {
			this.attemptRetry();
		}, this.options.delayMs);
	}

	/**
	 * Attempts to retry the request if the maximum number of retries hasn't been reached.
	 *
	 * Checks for errors using the errorChecker function. If an error is detected:
	 * - Aborts the current request
	 * - Calls the beforeRetryCallback
	 * - Starts a new request with retry logic
	 * - Increments the retry counter
	 *
	 * If no error is detected, schedules another error check after the delay period.
	 *
	 * @private
	 */
	private attemptRetry() {
		if (this.retries < this.options.maxRetries) {
			const hasErrored = this.options.errorChecker();
			/**
			 * If errored then abort current request and start a new request.
			 */
			if (hasErrored) {
				this.callBeforeRetryCallback();
				this.startFetchingWithRetry();
			} else {
				if (this.currentRetryCheck < this.maxRetryChecks) {
					// If not errored then check again after delay.
					this.startErrorCheckTimer();
				}
				this.currentRetryCheck++;
			}
		}
	}

	private callBeforeRetryCallback() {
		this.abortController?.abort();
		/**
		 * In beforeRetryCallback, retryAttemptCount can be used for various purposes,
		 * 	like creating new ADFStreamer instance having retryAttemptCount as a parameter
		 * 	to send to analytics.
		 * So must update retries before calling beforeRetryCallback.
		 */
		this.retries++;
		this.options.beforeRetryCallback();
	}

	/**
	 * Manually triggers a retry when streaming has ended.
	 * This is useful when streaming ends before first check for errors
	 * 	or if ADFStreamer is in error state when streaming ends.
	 *
	 * It will only retry if ADFStreamer is in error state and
	 *  the maximum number of retries hasn't been reached.
	 *
	 * Steps performed:
	 * 1. Clears the error checking timer
	 * 2. Calls the beforeRetryCallback to prepare for retry
	 * 3. Initiates the new request using the fetcher function
	 * 4. Increments the retry counter
	 *
	 * @public
	 */
	retryOnStreamingEnd(): void {
		this.clearErrorCheckTimer();
		const hasErrored = this.options.errorChecker();
		if (hasErrored && !this.hasMaxRetryReached()) {
			this.callBeforeRetryCallback();
			this.fetch();
		}
	}

	/**
	 * Returns the current number of retry attempts made.
	 *
	 * @public
	 */
	getRetryCount(): number {
		return this.retries;
	}

	/**
	 * Checks if the maximum number of retries has been reached.
	 *
	 * @public
	 */
	hasMaxRetryReached(): boolean {
		return this.retries >= this.options.maxRetries;
	}

	/**
	 * Creates a new AbortController, initiates the fetch using the fetcher function.
	 *
	 * @private
	 */
	private fetch() {
		this.abortController = new AbortController();
		this.options.fetcher(this.abortController, this);
	}

	/**
	 * Starts a new fetch operation with retry monitoring enabled.
	 *
	 * Creates a new AbortController, initiates the fetch using the fetcher function,
	 * and starts the error checking timer to monitor for issues during the request.
	 */
	startFetchingWithRetry(): void {
		this.currentRetryCheck = 0;
		this.fetch();
		this.startErrorCheckTimer();
	}

	/**
	 * Clears the active error checking timer if one exists.
	 *
	 * This stops any pending error checks and resets the timer ID to null.
	 * Used during cleanup or when resetting the retry manager state.
	 *
	 * @private
	 */
	private clearErrorCheckTimer() {
		if (this.errorCheckTimerId) {
			clearTimeout(this.errorCheckTimerId);
			this.errorCheckTimerId = null;
		}
	}

	/**
	 * Resets the retry manager to its initial state.
	 *
	 * Clears any active error checking timers and resets the retry counter to zero.
	 * This allows the retry manager to be reused for new streaming operations.
	 *
	 * @public
	 */
	reset(): void {
		this.clearErrorCheckTimer();
		this.retries = 0;
	}
}
